import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const API_BASE = 'https://birdseye-main-326804163209.asia-southeast1.run.app';

const navIcons = {
  Dashboard: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  Cameras: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
  History: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="12 8 12 12 14 14" />
      <path d="M3.05 11a9 9 0 1 0 .5-4.5" />
      <polyline points="3 3 3 7 7 3" />
    </svg>
  ),
  Footage: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M10 11l5 3-5 3v-6z" />
    </svg>
  ),
  Profile: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Settings: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  Team: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="3" />
      <path d="M3 20c1.5-3 4-4.5 6-4.5s4.5 1.5 6 4.5" />
      <path d="M17 11c1 0 2 .8 2 2v1" />
    </svg>
  ),
};

const Team = () => {
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [view, setView] = useState('list'); // 'list' or 'code'
  const [code, setCode] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) navigate('/login');
  }, [navigate]);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/users/farm-members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : (json?.members || json?.data || []);
      setMembers(list);
    } catch (err) {
      console.warn('Fetch members failed:', err.message);
      setError('Could not load team members.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleDelete = async (member) => {
    const userId = member?.id ?? member?.user_id;
    if (!userId) return;
    if (!window.confirm('Remove team member? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // remove locally
      setMembers((m) => m.filter(x => (x.id ?? x.user_id) !== userId));
    } catch (err) {
      console.warn('Delete failed:', err.message);
      setError('Could not delete member.');
    }
  };

  const enterCodeView = async () => {
    setView('code');
    setCode(null);
    setCodeError(null);
    setCodeLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/auth/create_activation_code?data=member`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const returned = json?.activation_code ?? json?.code ?? json?.data ?? json?.activationCode ?? json;
      const codeStr = typeof returned === 'string' ? returned : (returned?.code || returned?.activation_code || '') ;
      setCode(codeStr || String(returned));
    } catch (err) {
      console.warn('Create code failed:', err.message);
      setCodeError('Could not generate activation code. Admin permissions required.');
    } finally {
      setCodeLoading(false);
    }
  };

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <motion.div className="h-screen flex overflow-hidden bg-[#F5EFE7]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      {/* Sidebar */}
      <div className="w-56 flex-shrink-0 p-3">
        <div className="h-full bg-[#AC4242] rounded-3xl flex flex-col">
          <div className="p-6 flex justify-center"><img src="/images/logonav.png" alt="Birdseye" className="h-32 w-auto" /></div>
          <nav className="flex-1 px-4 space-y-1">
            {[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Cameras', path: '/cameras' },
              { label: 'History', path: '/history' },
              { label: 'Footage', path: '/footage' },
              { label: 'Team', path: '/team' },
              { label: 'Profile', path: '/profile' },
              { label: 'Settings', path: '/settings' },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-3 px-4 py-3 w-full text-left text-white rounded-xl font-poppins text-sm transition-all duration-200 hover:scale-[1.02] ${item.label === 'Team' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'}`}
              >
                {navIcons[item.label]}
                {item.label}
              </button>
            ))}
          </nav>
          <div className="px-4 pb-6">
            <button onClick={() => { localStorage.removeItem('authToken'); localStorage.removeItem('userEmail'); navigate('/login'); }} className="flex items-center gap-3 px-4 py-3 w-full text-left text-white/60 hover:text-white hover:bg-white/10 rounded-xl font-poppins text-sm transition-all duration-200">Logout</button>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-[#F5EFE7] border-b border-black/[0.07] px-8 py-4 flex items-center justify-between flex-shrink-0">
          <img src="/images/loginlogo.png" alt="Birdseye" className="h-12 w-auto" />
          <span className="text-xs font-poppins text-gray-400">{localStorage.getItem('userEmail') || ''}</span>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-7">
          <div className="flex items-center justify-between mb-7">
            <h1 className="text-5xl font-poppins font-bold text-[#AC4242] tracking-tight leading-none">Team</h1>
            <div />
          </div>

          {error && <div className="mb-6 bg-red-50 border border-red-100 rounded-2xl px-5 py-3 flex items-center gap-3 text-sm font-poppins text-red-600">{error}</div>}

          {view === 'list' && (
            <div className="rounded-2xl p-6 bg-white border border-black/[0.04]">
              {loading ? (
                <div className="py-8 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#AC4242]/30 border-t-[#AC4242] rounded-full animate-spin" /></div>
              ) : members.length === 0 ? (
                <div className="py-16 px-6 text-center"><p className="text-sm font-poppins text-gray-400">No team members yet.</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th colSpan={2} className="p-0">
                          <div className="grid grid-cols-[140px_minmax(0,1fr)_120px_200px_260px_80px] px-6 py-4 text-left">
                            <span className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400">ROLE</span>
                            <span className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400">NAME</span>
                            <span className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400">ID</span>
                            <span className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400">PHONE NUMBER</span>
                            <span className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400">EMAIL</span>
                            <span className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400">&nbsp;</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => {
                        const userId = m?.id ?? m?.user_id ?? '';
                        const role = m?.role ?? m?.user_role ?? (m?.is_admin ? 'Admin' : 'Member');
                        const name = (m?.name ?? `${(m?.first_name || '')} ${(m?.last_name || '')}`.trim()) || (m?.email || '—');
                        const phone = m?.phone_number || m?.phone || '—';
                        const email = m?.email || '—';
                        return (
                          <tr key={userId} className="border-b border-gray-100 last:border-0">
                            <td colSpan={2} className="p-0">
                              <div className="grid grid-cols-[140px_minmax(0,1fr)_120px_200px_260px_80px] w-full text-left px-6 py-4 items-center">
                                <div className="text-sm font-poppins text-gray-700">{role}</div>
                                <div className="text-sm font-poppins font-semibold text-gray-800 truncate">{name}</div>
                                <div className="text-sm font-poppins text-gray-600">{userId}</div>
                                <div className="text-sm font-poppins text-gray-600">{phone || '—'}</div>
                                <div className="text-sm font-poppins text-gray-600 truncate">{email}</div>
                                <div className="text-right">
                                  <button onClick={() => handleDelete(m)} className="text-sm font-poppins text-red-600 hover:text-red-700 px-3 py-1 rounded-full">Delete</button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button onClick={enterCodeView} className="flex items-center gap-2 bg-white border border-black/[0.08] rounded-2xl px-5 py-2.5 text-sm font-poppins font-semibold text-gray-700 shadow-sm hover:shadow-md">Recruit Worker +</button>
              </div>
            </div>
          )}

          {view === 'code' && (
            <div className="rounded-2xl p-6 bg-white border border-black/[0.04]">
              <div className="mb-4">
                <button onClick={() => { setView('list'); setCode(null); setCodeError(null); }} className="text-sm font-poppins text-gray-600 underline">Back</button>
              </div>

              {codeLoading ? (
                <div className="py-8 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#AC4242]/30 border-t-[#AC4242] rounded-full animate-spin" /></div>
              ) : codeError ? (
                <div className="mb-4 bg-red-50 border border-red-100 rounded-2xl px-4 py-3 text-sm font-poppins text-red-600">{codeError}</div>
              ) : (
                <div className="flex flex-col items-center gap-4 py-8">
                  <div className="px-8 py-6 rounded-full bg-gradient-to-r from-[#FFF7F7] to-white border border-[#FAD0D0] shadow-sm text-center">
                    <p className="text-3xl md:text-4xl font-poppins font-semibold text-[#AC4242] tracking-wider">{code || '—'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={copyCode} className="bg-white border border-black/[0.08] rounded-2xl px-4 py-2 text-sm font-poppins font-semibold text-gray-700">{copied ? 'Copied!' : 'Copy code'}</button>
                    <button onClick={() => { setView('list'); fetchMembers(); }} className="bg-white border border-black/[0.08] rounded-2xl px-4 py-2 text-sm font-poppins">Back to members</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Team;
